// Issue keys, as today's pages read them (lib/keys.ts there). A key is its
// project's key, a hyphen, and the issue's number in three digits at least:
// `Poulpe-001`, `Poulpe-042`, `Poulpe-1000` (vocab.ts, issueKey).
//
// A key is written one way and read loosely: `poulpe-1`, `POULPE-001` and
// `Poulpe-0001` all name `Poulpe-001`. `writeKey` is the one place that writes
// a key and `readKey` the one that reads one. And the pages' addresses, for
// what a move between two of them keeps.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Whether `text` is an id as the server writes it (8-4-4-4-12 hex digits). */
export const isUuid = (text: string): boolean => UUID.test(text)

/** A key name's length at most: what the board's card holds on one line beside a four-digit number. */
const KEY_NAME_MOST = 12
const NAME = `[A-Za-z][A-Za-z0-9]{0,${KEY_NAME_MOST - 1}}`
const NUMBER = '[0-9]{1,9}'
const KEY = new RegExp(`^(${NAME})-(${NUMBER})$`)
/** A key name: a letter, then letters and digits. */
export const KEY_NAME = new RegExp(`^${NAME}$`)

/** The one spelling of a key: the project's key name as it is kept, and the number in three digits at least. */
export function writeKey(name: string, number: number): string {
  return `${name}-${String(number).padStart(3, '0')}`
}

/**
 * A key as anybody may write it: its key name as written, in whatever case,
 * and its number, whatever zeros lead it. Null for anything else.
 */
export function readKey(text: string): { name: string; number: number } | null {
  const found = KEY.exec(text.trim())
  return found ? { name: found[1]!, number: Number(found[2]) } : null
}

/** A key of one of these projects, read loosely and written the one way; null when `text` names none of them. */
export function spellKey(text: string, names: string | readonly string[]): string | null {
  const read = readKey(text)
  if (!read || read.number < 1) return null
  const name = (typeof names === 'string' ? [names] : names).find((kept) => kept.toLowerCase() === read.name.toLowerCase())
  return name === undefined ? null : writeKey(name, read.number)
}

/** What every spelling of one key shares; null for what is no key. */
export function keyId(text: string): string | null {
  const read = readKey(text)
  return read ? `${read.name.toLowerCase()}-${read.number}` : null
}

/** A key found in a text: where it starts, how much of the text spells it, and the key written the one way. */
export interface KeyMatch {
  index: number
  /** Not the written key's length: `poulpe-12` spells `Poulpe-012`. */
  length: number
  key: string
}

const WORD_CHAR = /[\p{L}\p{N}_]/u
const escapeName = (name: string) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Every key of these projects in `text` that stands on its own: not glued to
 * a word before it (`XPoulpe-1`, `pre-Poulpe-1`), nor running into one after
 * (`Poulpe-1a`). In any spelling, each written the one way (today's `keysIn`).
 */
export function keysIn(text: string, names: string | readonly string[]): KeyMatch[] {
  const kept = typeof names === 'string' ? [names] : names
  if (kept.length === 0) return []
  const pattern = new RegExp(`(?:${kept.map(escapeName).join('|')})-${NUMBER}`, 'gi')
  const matches: KeyMatch[] = []
  for (const found of text.matchAll(pattern)) {
    const before = found.index > 0 ? text[found.index - 1]! : ''
    const after = text[found.index + found[0].length] ?? ''
    if (before && (WORD_CHAR.test(before) || before === '-')) continue
    if (after && WORD_CHAR.test(after)) continue
    const key = spellKey(found[0], kept)
    if (key) matches.push({ index: found.index, length: found[0].length, key })
  }
  return matches
}

/** Whether two spellings name one key. */
export function sameKey(one: string, other: string): boolean {
  const id = keyId(one)
  return id !== null && id === keyId(other)
}

/** A route segment that addresses an issue: a key of these projects in any spelling, or a UUID. */
export function isIssueRef(segment: string, names: string | readonly string[] = []): boolean {
  return spellKey(segment, names) !== null || UUID.test(segment)
}

/**
 * The page a path shows, for what a move between two paths keeps (unsaved
 * text, the scroll): Needs you answers at `/` and at `/r/{id}`, and the
 * Code tab at `/code` and at each session's `/code/{id}`.
 */
export function pageOf(pathname: string): string {
  if (pathname.startsWith('/r/')) return '/'
  if (pathname.startsWith('/code/')) return '/code'
  const code = /^(\/p\/[^/]+\/code)\//.exec(pathname)
  if (code) return code[1]!
  return pathname
}

/** Settings, the sheet drawn over the page that was there: that page stays under it. */
export function isSheet(pathname: string): boolean {
  return pathname === '/settings' || pathname.startsWith('/settings/') || pathname === '/setup'
}

/**
 * The pages a move from `from` to `to` throws away, for what each owns (unsaved
 * text, drafts): the page under the sheet only when another page takes its
 * place, the sheet's section only when it closes or shows another. `under` is
 * the page drawn under `from` while `from` is the sheet.
 */
export function pagesLeft(from: string, to: string, under: string): string[] {
  const below = pageOf(isSheet(from) ? under : from)
  const left: string[] = []
  if (!isSheet(to) && pageOf(to) !== below) left.push(below)
  if (isSheet(from) && pageOf(to) !== pageOf(from)) left.push(pageOf(from))
  return left
}
